import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, EMPTY, map, of, startWith, switchMap } from 'rxjs';
import {
  deleteLeagueResultModalActions,
  divisionDetailsComponentActions,
  editLeagueResultModalActions,
  leagueApiActions,
  leagueOverviewComponentActions,
} from './league.actions';
import { LeagueApiClient } from '@pool-league/frontend-shared-data-api';
import { Store } from '@ngrx/store';

@Injectable()
export class LeagueEffects {
  readonly #actions$ = inject(Actions);
  readonly #leagueApiClient = inject(LeagueApiClient);
  readonly #store = inject(Store);

  readonly loadDivisions = createEffect(() =>
    this.#actions$.pipe(
      ofType(leagueOverviewComponentActions.loadDivisions),
      switchMap(({ seasonId, format }) =>
        this.#leagueApiClient.getDivisions(seasonId, format).pipe(
          map((divisions) =>
            leagueApiActions.divisionsLoadSuccess({ divisions }),
          ),
          catchError((error) =>
            of(leagueApiActions.divisionsLoadError({ error })),
          ),
          startWith(leagueApiActions.divisionsLoading()),
        ),
      ),
    ),
  );

  readonly loadDivision = createEffect(() =>
    this.#actions$.pipe(
      ofType(
        divisionDetailsComponentActions.loadDivision,
        divisionDetailsComponentActions.unloadDivision,
      ),
      switchMap((action) => {
        if (
          action.type === divisionDetailsComponentActions.unloadDivision.type
        ) {
          // switchmap to empty when we navigate away from the division details
          // page so that we drop any inflight requests so they don't interfere
          // with future state (e.g., don't update the division of the wrong
          // season, if the user has since navigated to a different season)
          return EMPTY;
        }
        const { seasonId, format, divisionId } = action;
        return this.#leagueApiClient
          .getDivision(seasonId, format, divisionId)
          .pipe(
            map((division) =>
              leagueApiActions.divisionLoadSuccess({ division }),
            ),
            catchError((error) =>
              of(leagueApiActions.divisionLoadError({ error })),
            ),
            startWith(leagueApiActions.divisionLoading()),
          );
      }),
    ),
  );

  readonly saveResult = createEffect(() =>
    this.#actions$.pipe(
      ofType(
        editLeagueResultModalActions.saveResult,
        editLeagueResultModalActions.modalClosed,
      ),
      switchMap((action) => {
        if (action.type === editLeagueResultModalActions.modalClosed.type) {
          return EMPTY;
        }

        const { seasonId, format, divisionId, matchNo, winner, result } =
          action.match;

        return this.#leagueApiClient
          .saveResult(seasonId, format, divisionId, matchNo, winner, result)
          .pipe(
            map(({ division, leagueProgress }) =>
              leagueApiActions.saveResultSuccess({ division, leagueProgress }),
            ),
            catchError((error) =>
              of(leagueApiActions.saveResultError({ error })),
            ),
            startWith(leagueApiActions.saveResultRequestSent()),
          );
      }),
    ),
  );

  readonly deleteResult = createEffect(() =>
    this.#actions$.pipe(
      ofType(
        deleteLeagueResultModalActions.deleteResult,
        deleteLeagueResultModalActions.modalClosed,
      ),
      switchMap((action) => {
        if (action.type === deleteLeagueResultModalActions.modalClosed.type) {
          return EMPTY;
        }
        const { seasonId, format, divisionId, matchNo } = action.match;
        return this.#leagueApiClient
          .deleteResult(seasonId, format, divisionId, matchNo)
          .pipe(
            map(({ division, leagueProgress }) =>
              leagueApiActions.deleteResultSuccess({
                division,
                leagueProgress,
              }),
            ),
            catchError((error) =>
              of(leagueApiActions.deleteResultError({ error })),
            ),
            startWith(leagueApiActions.deleteResultRequestSent()),
          );
      }),
    ),
  );
}
